import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Offer, OfferStatus, OfferSection, OfferSectionType, OfferPosition,
  Client, Project, CreateOfferRequest, UpdateOfferRequest, OfferEmail, OfferDocument,
} from '../types';
import offerService from '../services/offerService';
import clientService, { composeRecipientAddress } from '../services/clientService';
import projectService from '../services/projectService';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

const statusVariant = (s: OfferStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case OfferStatus.DRAFT: return 'amber';
    case OfferStatus.SENT: return 'purple';
    case OfferStatus.ACCEPTED: return 'green';
    case OfferStatus.REJECTED: return 'red';
    case OfferStatus.EXPIRED: return 'red';
  }
};

const statusLabel = (s: OfferStatus): string => ({
  [OfferStatus.DRAFT]: 'Entwurf',
  [OfferStatus.SENT]: 'Versendet',
  [OfferStatus.ACCEPTED]: 'Angenommen',
  [OfferStatus.REJECTED]: 'Abgelehnt',
  [OfferStatus.EXPIRED]: 'Abgelaufen',
}[s]);

const allowedTransitions = (s: OfferStatus): OfferStatus[] => {
  switch (s) {
    case OfferStatus.DRAFT: return [OfferStatus.SENT];
    case OfferStatus.SENT: return [OfferStatus.ACCEPTED, OfferStatus.REJECTED];
    case OfferStatus.EXPIRED: return [OfferStatus.DRAFT];
    default: return [];
  }
};

const emptySection = (sortOrder: number): OfferSection => ({
  sortOrder,
  title: '',
  type: OfferSectionType.ONE_TIME,
  subtitle: 'einmalige Leistung',
  positions: [],
});

const emptyPosition = (sortOrder: number, number: string): OfferPosition => ({
  sortOrder,
  positionNumber: number,
  title: '',
  description: '',
  unitPriceCents: 0,
  quantity: 1,
});

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const emptyOffer = (): Offer => ({
  clientId: 0,
  number: '',
  title: '',
  offerDate: todayIso(),
  status: OfferStatus.DRAFT,
  vatRatePercent: 19,
  active: true,
  sections: [],
});

/** Eingabe in EUR (mit Komma oder Punkt) → Cent als Zahl. */
const euroInputToCents = (input: string): number => {
  const cleaned = input.replace(/[^0-9,.\-]/g, '').replace(',', '.');
  const value = parseFloat(cleaned);
  if (isNaN(value)) return 0;
  return Math.round(value * 100);
};

const centsToInput = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');

const formatEuro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' EUR';

const OfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  usePageTitle(isNew ? 'Neues Angebot' : 'Angebot bearbeiten');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [offer, setOffer] = useState<Offer>(emptyOffer());
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  // Gespeicherter Stand: verschickt wird immer der, also darf vorher nichts offen sein.
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');
  const [emails, setEmails] = useState<OfferEmail[]>([]);
  const [sendForm, setSendForm] = useState<{
    to: string; cc: string; subject: string; message: string;
    attachUsageAgreement: boolean; attachAvv: boolean;
  } | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const clientList = await clientService.getAll();
        setClients(clientList);
        if (!isNew && id) {
          const loaded = await offerService.getById(Number(id));
          setOffer(loaded);
          setSavedSnapshot(JSON.stringify(loaded));
          offerService.emails(Number(id)).then(setEmails).catch(() => {});
        } else if (isNew) {
          try {
            const suggested = await offerService.nextNumber();
            setOffer(o => ({ ...o, number: suggested }));
          } catch { /* fallback: leeres Feld */ }
        }
      } catch (e) {
        toast.error('Daten konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  // Lade Projekte des aktuell gewählten Kunden
  useEffect(() => {
    if (!offer.clientId) { setProjects([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await projectService.getAll(offer.clientId);
        if (!cancelled) setProjects(list);
      } catch (e) {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [offer.clientId]);

  const updateOffer = (patch: Partial<Offer>) => setOffer(o => ({ ...o, ...patch }));

  // Bei Kundenwahl Ansprechpartner + Empfänger-Anschrift aus Stammdaten vorbefüllen,
  // ohne bereits eingetragene Werte zu überschreiben.
  const selectClient = (id: number) => setOffer(o => {
    const client = clients.find(c => c.id === id);
    return {
      ...o,
      clientId: id,
      projectId: null,
      contactPerson: o.contactPerson?.trim() ? o.contactPerson : (client?.contactPerson ?? o.contactPerson),
      recipientAddress: o.recipientAddress?.trim()
        ? o.recipientAddress
        : (client ? composeRecipientAddress(client) : o.recipientAddress),
    };
  });

  const updateSection = (idx: number, patch: Partial<OfferSection>) => {
    setOffer(o => {
      const sections = [...o.sections];
      sections[idx] = { ...sections[idx], ...patch };
      return { ...o, sections };
    });
  };

  const updatePosition = (sIdx: number, pIdx: number, patch: Partial<OfferPosition>) => {
    setOffer(o => {
      const sections = [...o.sections];
      const positions = [...sections[sIdx].positions];
      positions[pIdx] = { ...positions[pIdx], ...patch };
      sections[sIdx] = { ...sections[sIdx], positions };
      return { ...o, sections };
    });
  };

  const addSection = () => {
    setOffer(o => ({
      ...o,
      sections: [...o.sections, emptySection(o.sections.length + 1)],
    }));
  };

  const removeSection = (idx: number) => {
    setOffer(o => ({ ...o, sections: o.sections.filter((_, i) => i !== idx) }));
  };

  const addPosition = (sIdx: number) => {
    setOffer(o => {
      const sections = [...o.sections];
      const section = sections[sIdx];
      const nextNumber = `${sIdx + 1}.${section.positions.length + 1}`;
      sections[sIdx] = {
        ...section,
        positions: [...section.positions, emptyPosition(section.positions.length + 1, nextNumber)],
      };
      return { ...o, sections };
    });
  };

  const removePosition = (sIdx: number, pIdx: number) => {
    setOffer(o => {
      const sections = [...o.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        positions: sections[sIdx].positions.filter((_, i) => i !== pIdx),
      };
      return { ...o, sections };
    });
  };

  const handleSave = async () => {
    if (!offer.clientId) { toast.error('Bitte Kunden wählen'); return; }
    if (!offer.number.trim()) { toast.error('Angebotsnummer fehlt'); return; }
    if (!offer.title.trim()) { toast.error('Titel fehlt'); return; }

    setSaving(true);
    try {
      const payload: CreateOfferRequest | UpdateOfferRequest = {
        clientId: offer.clientId,
        projectId: offer.projectId ?? null,
        number: offer.number,
        title: offer.title,
        contactPerson: offer.contactPerson ?? null,
        recipientAddress: offer.recipientAddress ?? null,
        offerDate: offer.offerDate,
        validUntil: offer.validUntil ?? null,
        introText: offer.introText ?? null,
        notesHtml: offer.notesHtml ?? null,
        vatRatePercent: offer.vatRatePercent,
        notesInternal: offer.notesInternal ?? null,
        sections: offer.sections,
        active: offer.active,
      };
      const saved = isNew
        ? await offerService.create(payload)
        : await offerService.update(Number(id), payload as UpdateOfferRequest);
      toast.success(isNew ? 'Angebot erstellt' : 'Gespeichert');
      if (isNew) navigate(`/offers/${saved.id}`);
      else { setOffer(saved); setSavedSnapshot(JSON.stringify(saved)); }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleTransition = async (newStatus: OfferStatus) => {
    if (isNew) return;
    try {
      const updated = await offerService.transitionStatus(Number(id), newStatus);
      // Nur Status übernehmen, damit ungespeicherte Änderungen im Formular erhalten bleiben
      setOffer(o => ({ ...o, status: updated.status, sentAt: updated.sentAt, acceptedAt: updated.acceptedAt,
        rejectedAt: updated.rejectedAt, expiredAt: updated.expiredAt }));
      setSavedSnapshot(s => {
        if (!s) return s;
        const parsed = JSON.parse(s);
        return JSON.stringify({ ...parsed, status: updated.status, sentAt: updated.sentAt,
          acceptedAt: updated.acceptedAt, rejectedAt: updated.rejectedAt, expiredAt: updated.expiredAt });
      });
      toast.success(`Status: ${statusLabel(newStatus)}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Status-Wechsel fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    if (isNew || !id) return;
    if (!window.confirm('Angebot wirklich löschen?')) return;
    try {
      await offerService.delete(Number(id));
      toast.success('Angebot gelöscht');
      navigate('/offers');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handlePreview = async (kind: 'offer' | 'usage-agreement' | 'avv') => {
    if (isNew) { toast.error('Bitte erst speichern'); return; }
    try {
      const html = kind === 'offer'
        ? await offerService.renderHtml(Number(id))
        : kind === 'avv'
          ? await offerService.renderAvvHtml(Number(id))
          : await offerService.renderUsageAgreementHtml(Number(id));
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // URL.revokeObjectURL(url); — wir lassen ihn offen, damit Print im neuen Tab funktioniert
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
    }
  };

  const isDirty = !isNew && savedSnapshot !== '' && JSON.stringify(offer) !== savedSnapshot;
  const canSend = !isNew && offer.status !== OfferStatus.REJECTED && offer.status !== OfferStatus.EXPIRED;

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const safeNumber = (offer.number || String(id)).replace(/[^A-Za-z0-9._-]/g, '_');

  const handleDownloadPdf = async (kind: 'offer' | 'usage-agreement') => {
    if (isNew) { toast.error('Bitte erst speichern'); return; }
    if (isDirty) { toast.error('Bitte erst speichern, das PDF zeigt den gespeicherten Stand'); return; }
    try {
      const blob = kind === 'offer'
        ? await offerService.renderPdf(Number(id))
        : await offerService.renderUsageAgreementPdf(Number(id));
      downloadBlob(blob, `${kind === 'offer' ? 'Angebot' : 'Nutzungsvereinbarung'}-${safeNumber}.pdf`);
    } catch (e) {
      toast.error('PDF konnte nicht erstellt werden');
    }
  };

  const handleDownloadArchive = async (email: OfferEmail, document: OfferDocument) => {
    try {
      const blob = await offerService.emailPdf(Number(id), email.id, document);
      const nr = email.offerNumber.replace(/[^A-Za-z0-9._-]/g, '_');
      const prefix = { OFFER: 'Angebot', USAGE_AGREEMENT: 'Nutzungsvereinbarung', AVV: 'Auftragsverarbeitung' }[document];
      downloadBlob(blob, `${prefix}-${nr}-versendet.pdf`);
    } catch (e) {
      toast.error('Archivkopie konnte nicht geladen werden');
    }
  };

  const openSendDialog = () => {
    if (isNew || !id) { toast.error('Bitte erst speichern'); return; }
    if (isDirty) { toast.error('Bitte erst speichern, verschickt wird der gespeicherte Stand'); return; }
    const client = clients.find(c => c.id === offer.clientId);
    setSendForm({
      to: client?.contactEmail?.trim() || '',
      cc: '',
      subject: `Angebot ${offer.number} – ${offer.title}`,
      message: '',
      // Laufende Leistungen (Hosting, Betrieb) haben eine Nutzungsvereinbarung
      attachUsageAgreement: offer.sections.some(s => s.type === OfferSectionType.RECURRING),
      // Betrieb mit laufenden Kosten = Hosting bei uns = Auftragsverarbeitung
      attachAvv: offer.sections.some(s => s.type === OfferSectionType.RECURRING),
    });
  };

  const handleSend = async () => {
    if (!sendForm || !id) return;
    if (!sendForm.to.trim()) { toast.error('Bitte einen Empfänger angeben'); return; }
    if (emails.length > 0 && !window.confirm(
      `Angebot ${offer.number} wurde bereits verschickt. Noch einmal an ${sendForm.to.trim()} senden?`
    )) return;

    setSending(true);
    try {
      const updated = await offerService.send(Number(id), {
        to: sendForm.to.trim(),
        cc: sendForm.cc.trim() || null,
        subject: sendForm.subject.trim() || null,
        message: sendForm.message.trim() || null,
        attachUsageAgreement: sendForm.attachUsageAgreement,
        attachAvv: sendForm.attachAvv,
      });
      setOffer(updated);
      setSavedSnapshot(JSON.stringify(updated));
      offerService.emails(Number(id)).then(setEmails).catch(() => {});
      setSendForm(null);
      toast.success(`Angebot an ${sendForm.to.trim()} verschickt`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Versand fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <PageHeader
        title={isNew ? 'Neues Angebot' : `Angebot ${offer.number}`}
        subtitle={offer.title}
        action={
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant={statusVariant(offer.status)}>{statusLabel(offer.status)}</Badge>
            {!isNew && allowedTransitions(offer.status).map(s => (
              <button
                key={s}
                onClick={() => handleTransition(s)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)', background: 'var(--surface-bg)' }}
              >
                → {statusLabel(s)}
              </button>
            ))}
          </div>
        }
      />

      <Card title="Allgemein">
        <Grid>
          <Field label="Angebotsnummer *">
            <Input value={offer.number} onChange={v => updateOffer({ number: v })} placeholder="2026-001" />
          </Field>
          <Field label="Kunde *">
            <Select value={offer.clientId} onChange={v => selectClient(Number(v))}>
              <option value={0}>— wählen —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Projekt (optional)">
            <Select value={offer.projectId ?? ''} onChange={v => updateOffer({ projectId: v === '' ? null : Number(v) })}>
              <option value="">— kein Projekt —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Angebotsdatum *">
            <Input type="date" value={offer.offerDate} onChange={v => updateOffer({ offerDate: v })} />
          </Field>
          <Field label="Gültig bis">
            <Input type="date" value={offer.validUntil ?? ''} onChange={v => updateOffer({ validUntil: v || null })} />
          </Field>
          <Field label="Titel / Betreff *" full>
            <Input value={offer.title} onChange={v => updateOffer({ title: v })}
              placeholder="Anpassung und Erweiterung der Software zur …" />
          </Field>
          <Field label="Ansprechpartner (Anrede)">
            <Input value={offer.contactPerson ?? ''} onChange={v => updateOffer({ contactPerson: v })}
              placeholder="Herrn Jakob Seiß" />
          </Field>
          <Field label="MwSt-Satz (%)">
            <IntegerInput value={offer.vatRatePercent} min={0}
              onChange={n => updateOffer({ vatRatePercent: n })} />
          </Field>
          <Field label="Empfänger-Anschrift (eine Zeile pro Adresszeile)" full>
            <Textarea value={offer.recipientAddress ?? ''} rows={4}
              onChange={v => updateOffer({ recipientAddress: v })}
              placeholder={'Landeskirchenamt\nHerrn Jakob Seiß\nDietrich-Bonhoeffer-Str. 1\n38300 Wolfenbüttel'} />
          </Field>
          <Field label="Einleitungstext (HTML erlaubt)" full>
            <Textarea value={offer.introText ?? ''} rows={3}
              onChange={v => updateOffer({ introText: v })}
              placeholder="vielen Dank für Ihre Anfrage. …" />
          </Field>
        </Grid>
      </Card>

      <Card title="Positionen" actions={
        <button onClick={addSection}
          className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
          + Abschnitt
        </button>
      }>
        {offer.sections.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Noch keine Abschnitte angelegt.</p>
        ) : offer.sections.map((section, sIdx) => {
          const netCents = section.positions.reduce((sum, p) => sum + p.unitPriceCents * p.quantity, 0);
          const vatCents = Math.round((netCents * offer.vatRatePercent) / 100);
          const grossCents = netCents + vatCents;
          return (
            <div key={sIdx} className="mb-6 p-4 rounded-lg border"
              style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-bg)' }}>
              <div className="flex flex-wrap gap-3 items-end mb-3">
                <Field label="Abschnittstitel *">
                  <Input value={section.title} onChange={v => updateSection(sIdx, { title: v })}
                    placeholder="Anpassung bestehender Software" />
                </Field>
                <Field label="Typ">
                  <Select value={section.type} onChange={v => updateSection(sIdx, { type: v as OfferSectionType })}>
                    <option value={OfferSectionType.ONE_TIME}>Einmalig</option>
                    <option value={OfferSectionType.RECURRING}>Monatlich</option>
                  </Select>
                </Field>
                <Field label="Untertitel">
                  <Input value={section.subtitle ?? ''} onChange={v => updateSection(sIdx, { subtitle: v })}
                    placeholder="einmalige Leistung" />
                </Field>
                <button onClick={() => removeSection(sIdx)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                  style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)' }}>
                  Abschnitt löschen
                </button>
              </div>

              <table className="w-full text-sm mb-3">
                <thead style={{ color: 'var(--text-muted)' }}>
                  <tr className="text-left">
                    <th className="px-2 py-1 w-16">Nr.</th>
                    <th className="px-2 py-1">Bezeichnung</th>
                    <th className="px-2 py-1 w-32">Einzelpreis (EUR)</th>
                    <th className="px-2 py-1 w-20">Anzahl</th>
                    <th className="px-2 py-1 w-28">Einheit</th>
                    <th className="px-2 py-1 w-32 text-right">Summe</th>
                    <th className="px-2 py-1 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {section.positions.map((pos, pIdx) => (
                    <React.Fragment key={pIdx}>
                      <tr className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                        <td className="px-2 py-1">
                          <Input value={pos.positionNumber} onChange={v => updatePosition(sIdx, pIdx, { positionNumber: v })} />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={pos.title} onChange={v => updatePosition(sIdx, pIdx, { title: v })}
                            placeholder="Position-Titel" />
                        </td>
                        <td className="px-2 py-1">
                          <PriceInput valueCents={pos.unitPriceCents}
                            onChange={cents => updatePosition(sIdx, pIdx, { unitPriceCents: cents })} />
                        </td>
                        <td className="px-2 py-1">
                          <IntegerInput value={pos.quantity}
                            onChange={n => updatePosition(sIdx, pIdx, { quantity: n })} />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={pos.unitLabel ?? ''} onChange={v => updatePosition(sIdx, pIdx, { unitLabel: v })}
                            placeholder="monatlich" />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold">
                          {formatEuro(pos.unitPriceCents * pos.quantity)}
                        </td>
                        <td className="px-2 py-1">
                          <button onClick={() => removePosition(sIdx, pIdx)}
                            className="text-xs px-2 py-1 rounded" style={{ color: 'var(--badge-red-text)' }}>
                            ✕
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={7} className="px-2 pb-3">
                          <Textarea value={pos.description ?? ''} rows={2}
                            placeholder="Beschreibung (HTML erlaubt)"
                            onChange={v => updatePosition(sIdx, pIdx, { description: v })} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-between items-center">
                <button onClick={() => addPosition(sIdx)}
                  className="text-xs px-3 py-1.5 rounded-lg border"
                  style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                  + Position
                </button>
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Netto: <strong>{formatEuro(netCents)}</strong>
                  {' · '}MwSt {offer.vatRatePercent}%: <strong>{formatEuro(vatCents)}</strong>
                  {' · '}Brutto: <strong style={{ color: 'var(--text-primary)' }}>{formatEuro(grossCents)}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </Card>

      <Card title="Konditionen und Hinweise">
        <Textarea value={offer.notesHtml ?? ''} rows={8}
          onChange={v => updateOffer({ notesHtml: v })}
          placeholder="HTML erlaubt. Z. B. <ul><li>Zahlungsbedingungen: 14 Tage netto.</li></ul>" />
      </Card>

      <Card title="Interne Notizen (nicht im PDF)">
        <Textarea value={offer.notesInternal ?? ''} rows={3}
          onChange={v => updateOffer({ notesInternal: v })} />
      </Card>

      {!isNew && emails.length > 0 && (
        <Card title="Versand">
          <div className="flex flex-col gap-3">
            {emails.map(e => (
              <div key={e.id} className="flex flex-wrap justify-between gap-3 text-sm pb-3 border-b last:border-b-0 last:pb-0"
                style={{ borderColor: 'var(--surface-border)' }}>
                <div>
                  <div className="font-semibold">
                    {new Date(e.sentAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                    {' · an '}{e.recipient}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    {e.cc ? `Kopie an ${e.cc} · ` : ''}„{e.subject}“ · von {e.sentBy}
                    {e.hasUsageAgreement ? ' · mit Nutzungsvereinbarung' : ''}
                    {e.hasAvv ? ' · mit AVV' : ''}
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <button onClick={() => handleDownloadArchive(e, 'OFFER')}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
                    title="PDF genau so, wie es verschickt wurde">
                    Angebot (versendet)
                  </button>
                  {e.hasUsageAgreement && (
                    <button onClick={() => handleDownloadArchive(e, 'USAGE_AGREEMENT')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                      Nutzungsvereinbarung
                    </button>
                  )}
                  {e.hasAvv && (
                    <button onClick={() => handleDownloadArchive(e, 'AVV')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                      AVV
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 justify-end mt-6">
        {!isNew && (
          <button onClick={handleDelete}
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)' }}>
            Löschen
          </button>
        )}
        <button onClick={() => navigate('/offers')}
          className="px-4 py-2 rounded-lg text-sm font-semibold border"
          style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
          Zurück
        </button>
        {!isNew && (
          <>
            <button onClick={() => handlePreview('offer')}
              className="px-4 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              Vorschau Angebot
            </button>
            <button onClick={() => handlePreview('usage-agreement')}
              className="px-4 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              Vorschau Nutzungsvereinbarung
            </button>
            <button onClick={() => handlePreview('avv')}
              className="px-4 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              Vorschau AVV
            </button>
            <button onClick={() => handleDownloadPdf('offer')}
              className="px-4 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              PDF herunterladen
            </button>
            {canSend && (
              <button onClick={openSendDialog}
                className="px-4 py-2 rounded-lg text-sm font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
                title={isDirty ? 'Erst speichern' : undefined}>
                ✉ Per E-Mail versenden
              </button>
            )}
          </>
        )}
        <button onClick={handleSave} disabled={saving}
          className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Speichert…' : 'Speichern'}
        </button>
      </div>

      {sendForm && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="offer-send-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setSendForm(null); }}
        >
          <div className="rounded-xl p-6 w-full max-w-lg"
            style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="offer-send-title" className="text-lg font-semibold mb-1 tracking-tight">
              Angebot {offer.number} versenden
            </h2>
            <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
              Das Angebot geht als PDF-Anhang von office@gsk-it.de raus. Eine Kopie des PDFs wird
              im Versandprotokoll abgelegt.
              {offer.status === OfferStatus.DRAFT
                ? ' Der Entwurf wird dabei auf „Versendet“ gesetzt.'
                : ' Der Status bleibt unverändert.'}
            </p>

            <div className="grid grid-cols-1 gap-4">
              <Field label="An *">
                <Input type="email" value={sendForm.to}
                  onChange={v => setSendForm({ ...sendForm, to: v })}
                  placeholder="kunde@example.de" />
              </Field>
              <Field label="Kopie an (CC, mit Komma getrennt)">
                <Input value={sendForm.cc} onChange={v => setSendForm({ ...sendForm, cc: v })} />
              </Field>
              <Field label="Betreff">
                <Input value={sendForm.subject} onChange={v => setSendForm({ ...sendForm, subject: v })} />
              </Field>
              <Field label="Nachricht (leer = Standardanschreiben)">
                <Textarea value={sendForm.message} rows={5}
                  onChange={v => setSendForm({ ...sendForm, message: v })}
                  placeholder="Leer lassen für den Standardtext mit Angebotsnummer, Beträgen und Gültigkeit." />
              </Field>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sendForm.attachUsageAgreement}
                  onChange={e => setSendForm({ ...sendForm, attachUsageAgreement: e.target.checked })} />
                Nutzungsvereinbarung als PDF anhängen
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sendForm.attachAvv}
                  onChange={e => setSendForm({ ...sendForm, attachAvv: e.target.checked })} />
                Vertrag zur Auftragsverarbeitung (AVV) als PDF anhängen
              </label>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setSendForm(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                Abbrechen
              </button>
              <button onClick={handleSend} disabled={sending}
                className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? 'Versendet…' : 'Jetzt senden'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

/* === Tiny UI Helpers === */

const Card: React.FC<{ title: string; actions?: React.ReactNode; children: React.ReactNode }> = ({ title, actions, children }) => (
  <div className="mb-6 p-5 rounded-xl border" style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-bg-elevated, var(--surface-bg))' }}>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{title}</h3>
      {actions}
    </div>
    {children}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const Field: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{label}</label>
    {children}
  </div>
);

const inputStyle: React.CSSProperties = {
  background: 'var(--input-bg, var(--surface-bg))',
  border: '1px solid var(--surface-border)',
  color: 'var(--text-primary)',
  borderRadius: '8px',
  padding: '6px 10px',
  fontSize: '14px',
  width: '100%',
};

const Input: React.FC<{
  value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}> = ({ value, onChange, type = 'text', placeholder }) => (
  <input type={type} value={value} placeholder={placeholder}
    onChange={e => onChange(e.target.value)} style={inputStyle} />
);

/** Preiseingabe in EUR — interner Text-State, damit Backspace & freie Eingabe funktionieren. */
const PriceInput: React.FC<{
  valueCents: number;
  onChange: (cents: number) => void;
}> = ({ valueCents, onChange }) => {
  const [text, setText] = useState<string>(centsToInput(valueCents));
  const [isEditing, setIsEditing] = useState(false);

  // Wenn der Preis von außen geändert wird (z. B. nach Save), Text aktualisieren — aber nur wenn nicht gerade getippt wird.
  useEffect(() => {
    if (!isEditing) setText(centsToInput(valueCents));
  }, [valueCents, isEditing]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      style={inputStyle}
      onFocus={e => {
        setIsEditing(true);
        e.target.select();
      }}
      onChange={e => {
        const raw = e.target.value;
        setText(raw);
        // Erlaube leere Eingabe und Minus, ohne Sofort-Reset
        if (raw === '' || raw === '-') { onChange(0); return; }
        const cleaned = raw.replace(/[^0-9,.\-]/g, '').replace(',', '.');
        const value = parseFloat(cleaned);
        if (!isNaN(value)) onChange(Math.round(value * 100));
      }}
      onBlur={() => {
        setIsEditing(false);
        setText(centsToInput(valueCents));
      }}
    />
  );
};

/** Ganzzahl-Eingabe (Anzahl etc.) mit Local Text State — leeres Feld erlaubt während Editing. */
const IntegerInput: React.FC<{
  value: number;
  onChange: (n: number) => void;
  min?: number;
}> = ({ value, onChange, min = 1 }) => {
  const [text, setText] = useState<string>(String(value));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) setText(String(value));
  }, [value, isEditing]);

  return (
    <input
      type="text"
      inputMode="numeric"
      value={text}
      style={inputStyle}
      onFocus={e => {
        setIsEditing(true);
        e.target.select();
      }}
      onChange={e => {
        const raw = e.target.value;
        setText(raw);
        if (raw === '') { onChange(min); return; }
        const n = parseInt(raw.replace(/[^0-9-]/g, ''), 10);
        if (!isNaN(n)) onChange(Math.max(min, n));
      }}
      onBlur={() => {
        setIsEditing(false);
        setText(String(value));
      }}
    />
  );
};

const Textarea: React.FC<{
  value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
}> = ({ value, onChange, rows = 3, placeholder }) => (
  <textarea value={value} placeholder={placeholder} rows={rows}
    onChange={e => onChange(e.target.value)}
    style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
);

const Select: React.FC<{
  value: string | number; onChange: (v: string) => void; children: React.ReactNode;
}> = ({ value, onChange, children }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
    {children}
  </select>
);

export default OfferDetail;
